import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, Instagram, Youtube, CheckCircle2 } from 'lucide-react';
import { getErrorMessage } from "@/lib/errors";

// TikTok icon (not in lucide-react)
const TikTokIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.86A8.16 8.16 0 0 0 21.51 11V7.55a4.79 4.79 0 0 1-1.92-.86z" />
  </svg>
);

const INTERESTS = ['Apparel', 'Events', 'Sponsorship', 'General'] as const;

const Schema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(100),
  email: z.string().trim().email('Enter a valid email').max(255),
  phone: z.string().trim().min(5, 'Enter a valid phone number').max(30),
  interest: z.enum(INTERESTS, { errorMap: () => ({ message: 'Pick an interest' }) }),
});

export default function WaitingList() {
  const { user, signOut } = useAuth();
  const [form, setForm] = useState({ name: '', email: '', phone: '', interest: '' });
  const [honeypot, setHoneypot] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const mountedAt = useRef(Date.now());

  useEffect(() => {
    document.title = 'ØUTLAWS — Join the Waiting List';
  }, []);

  const update = (k: keyof typeof form) => (v: string) =>
    setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (honeypot) return; // bot
    if (Date.now() - mountedAt.current < 2500) {
      toast.error('Please take a moment before submitting.');
      return;
    }
    const parsed = Schema.safeParse(form);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0].message);
      return;
    }
    setSubmitting(true);
    try {
      const id = (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

      const { error } = await supabase
        .from('contact_submissions')
        .insert({
          id,
          name: parsed.data.name,
          email: parsed.data.email,
          phone: parsed.data.phone,
          interest: parsed.data.interest,
        });
      if (error) throw error;

      // Send notification email (await so we surface failures)
      const { error: emailError } = await supabase.functions.invoke(
        'send-waiting-list-notification',
        {
          body: {
            submissionId: id,
            name: parsed.data.name,
            email: parsed.data.email,
            phone: parsed.data.phone,
            interest: parsed.data.interest,
          },
        }
      );

      if (emailError) {
        console.error('Notification email failed', emailError);
        toast.success("You're on the list!", {
          description: 'Submission saved. (Email notification delayed — our team will still see it.)',
        });
      } else {
        toast.success("You're on the list!", {
          description: 'A notification has been sent to our team.',
        });
      }
      setDone(true);
      setForm({ name: '', email: '', phone: '', interest: '' });
    } catch (err) {
      console.error(err);
      toast.error(getErrorMessage(err) || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      {/* Subtle red top accent */}
      <div className="h-1 w-full bg-primary" />

      <main className="flex-1 flex items-center justify-center px-4 py-12 sm:py-20">
        <div className="w-full max-w-xl">
          {/* Logo / heading */}
          <div className="text-center mb-10">
            <h1 className="text-5xl sm:text-6xl font-serif tracking-[0.25em] text-foreground">
              ØUTLAWS
            </h1>
            <div className="mt-3 h-px w-16 bg-primary mx-auto" />
            <p className="mt-6 text-2xl sm:text-3xl font-serif italic text-foreground">
              Something is coming.
            </p>
            <p className="mt-3 text-sm sm:text-base text-muted-foreground tracking-wide uppercase">
              Join the waiting list
            </p>
          </div>

          {done ? (
            <div
              role="status"
              aria-live="polite"
              className="bg-card border border-primary/40 rounded-lg p-8 text-center shadow-[0_0_0_1px_hsl(var(--primary)/0.15)]"
            >
              <CheckCircle2 className="w-12 h-12 text-primary mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Submission received.</h2>
              <p className="text-sm text-muted-foreground">
                Thanks for reaching out — you're officially on the waiting list.
              </p>
              <div className="mt-5 mx-auto max-w-sm rounded-md border border-border bg-background/60 p-4 text-left">
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">
                  Notification sent
                </p>
                <p className="text-sm text-foreground">
                  An email confirmation of your enquiry was dispatched to the{' '}
                  <span className="text-primary font-medium">ØUTLAWS team</span>.
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Our team will follow up at the email address you provided.
                </p>
              </div>
              <Button
                variant="outline"
                className="mt-6"
                onClick={() => setDone(false)}
              >
                Submit another
              </Button>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              className="bg-card border border-border rounded-lg p-6 sm:p-8 space-y-5"
            >
              {/* Honeypot */}
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                style={{ position: 'absolute', left: '-9999px', width: 1, height: 1 }}
                aria-hidden="true"
              />

              <div className="space-y-2">
                <Label htmlFor="wl-name">Name</Label>
                <Input
                  id="wl-name"
                  value={form.name}
                  onChange={(e) => update('name')(e.target.value)}
                  placeholder="Your full name"
                  autoComplete="name"
                  maxLength={100}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="wl-email">Email</Label>
                <Input
                  id="wl-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => update('email')(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  maxLength={255}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="wl-phone">Phone</Label>
                <Input
                  id="wl-phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => update('phone')(e.target.value)}
                  placeholder="+27 ..."
                  autoComplete="tel"
                  maxLength={30}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="wl-interest">I'm interested in</Label>
                <Select value={form.interest} onValueChange={update('interest')}>
                  <SelectTrigger id="wl-interest">
                    <SelectValue placeholder="Choose one" />
                  </SelectTrigger>
                  <SelectContent>
                    {INTERESTS.map((i) => (
                      <SelectItem key={i} value={i}>{i}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground tracking-widest uppercase h-11"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Join the waiting list'}
              </Button>

              <p className="text-[11px] text-muted-foreground text-center pt-2">
                By submitting, you agree to be contacted by Outlaws SA regarding your enquiry.
              </p>
            </form>
          )}

          {/* Socials + admin link */}
          <div className="mt-10 flex flex-col items-center gap-5">
            <div className="flex items-center gap-5 text-muted-foreground">
              <a
                href="https://www.instagram.com/outlaws_sa/?hl=en"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="hover:text-primary transition-colors"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://www.youtube.com/@%C3%98UTLAWSSA"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
                className="hover:text-primary transition-colors"
              >
                <Youtube className="w-5 h-5" />
              </a>
              <a
                href="https://www.tiktok.com/@outlaws_sa3"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="TikTok"
                className="hover:text-primary transition-colors"
              >
                <TikTokIcon className="w-5 h-5" />
              </a>
            </div>
            <p className="text-[11px] text-muted-foreground/70 text-center">
              © {new Date().getFullYear()} Outlaws SA (Pty) Ltd ·{' '}
              <Link to="/auth" className="hover:text-primary transition-colors">Admin</Link>
              {user && (
                <>
                  {' · '}
                  <button
                    type="button"
                    onClick={() => signOut()}
                    className="hover:text-primary transition-colors underline-offset-2 hover:underline"
                  >
                    Sign out ({user.email})
                  </button>
                </>
              )}
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
